import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import { AuthChangeEvent } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fonction pour gérer les redirections après l'authentification OAuth
  const handleRedirectAfterAuth = async () => {
    // Forcer la réinitialisation de l'indicateur de redirection
    localStorage.removeItem('redirectionInProgress');
    
    // Restaurer le flag isNewUser depuis sessionStorage si nécessaire
    if (!localStorage.getItem('isNewUser') && sessionStorage.getItem('isNewUser_persisted') === 'true') {
      console.log("[Auth] Restoring isNewUser flag from sessionStorage");
      localStorage.setItem('isNewUser', 'true');
    }
    
    const redirectPath = localStorage.getItem('redirectAfterAuth');
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    const isNewUserFlag = localStorage.getItem('isNewUser') === 'true';
    
    console.log("[Auth] Handling redirect after auth:", {
      redirectPath,
      pendingRelationId,
      currentPath: window.location.pathname,
      hash: window.location.hash,
      isNewUser: isNewUserFlag
    });
    
    // OPTIMISATION: Si on a déjà le flag isNewUser, rediriger immédiatement vers SignUpFlow
    // sans attendre la vérification en base de données pour accélérer le processus
    if (isNewUserFlag) {
      console.log("[Auth] New user flag found in localStorage, redirecting immediately to signup flow");
      sessionStorage.removeItem('isNewUser_persisted');
      window.location.replace(`${window.location.origin}/#/signup-flow`);
      return;
    }
    
    // Vérifier si l'utilisateur est nouveau directement en base
    let isNewUser = false;
    try {
      if (user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_new_user')
          .eq('id', user.id)
          .single();
        
        if (!error && data && data.is_new_user) {
          console.log("[Auth] User is marked as new in database");
          isNewUser = true;
        }
      }
    } catch (err) {
      console.error("[Auth] Error checking if user is new:", err);
      // Fallback au localStorage si erreur
      isNewUser = isNewUserFlag;
    }
    
    // Utiliser location.replace au lieu de window.location.href pour une redirection plus fiable
    try {
      // Priorité à la redirection vers une page de partage de relation
      if (pendingRelationId) {
        const shareUrl = `/share/${pendingRelationId}`;
        console.log("[Auth] Redirecting to share page:", shareUrl);
        window.location.replace(`${window.location.origin}/#${shareUrl}`);
        return;
      } 
      // Sinon utiliser le chemin de redirection stocké
      else if (redirectPath) {
        console.log("[Auth] Redirecting to saved path:", redirectPath);
        localStorage.removeItem('redirectAfterAuth');
        window.location.replace(`${window.location.origin}/#${redirectPath}`);
        return;
      }
      // Pour un nouvel utilisateur sans relation ou chemin spécifique, rediriger vers signup-flow
      else if (isNewUser) {
        console.log("[Auth] New user detected, redirecting to signup flow");
        // Supprimer le flag de sessionStorage une fois la redirection effectuée
        sessionStorage.removeItem('isNewUser_persisted');
        window.location.replace(`${window.location.origin}/#/signup-flow`);
        return;
      }
      // Redirection par défaut vers le profil
      else {
        console.log("[Auth] No specific redirect path, going to profile");
        localStorage.removeItem('redirectAfterAuth');
        localStorage.removeItem('pendingRelationId');
        window.location.replace(`${window.location.origin}/#/profile`);
        return;
      }
    } catch (error) {
      console.error("[Auth] Error during redirect:", error);
    }
  };

  useEffect(() => {
    // Variable pour suivre si le composant est monté
    let isMounted = true;
    let sessionChecked = false;
    
    // Vérifier si l'utilisateur a une session persistante
    const hasPersistentSession = localStorage.getItem('session_persistence') === 'true';
    
    // Ajout d'une vérification pour éviter les boucles de redirection
    const isRedirectionLoop = sessionStorage.getItem('redirection_attempt_count');
    let redirectionCount = isRedirectionLoop ? parseInt(isRedirectionLoop) : 0;
    
    // Si trop de redirections en peu de temps, réinitialiser le système
    if (redirectionCount > 3) {
      console.log("[Auth] Trop de redirections détectées, réinitialisation du système");
      sessionStorage.removeItem('redirection_attempt_count');
      localStorage.removeItem('redirectAfterAuth');
      localStorage.removeItem('redirectionInProgress');
      localStorage.removeItem('pendingRelationId');
    }
    
    // Get initial session
    if (!sessionChecked) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!isMounted) return;
        
        if (session?.user) {
          // Si l'utilisateur a une session et qu'elle doit être persistante
          if (hasPersistentSession) {
            // Rafraîchir le token automatiquement
            supabase.auth.refreshSession();
          }
          
          setUser(session.user);
          // Vérifier si nous venons d'une authentification OAuth (présence d'un paramètre dans l'URL)
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
          const isOAuthCallback = urlParams.has('code') || 
                                urlParams.has('provider') || 
                                urlParams.has('access_token') ||
                                hashParams.has('access_token');
          
          console.log("Session loaded, isOAuthCallback:", isOAuthCallback);
          
          if (isMounted) {
            ensureProfileExists(session.user)
              .then(() => {
                if (isMounted) {
                  if (isOAuthCallback) {
                    console.log("OAuth callback detected, handling redirect");
                    // Nettoyer l'URL des paramètres OAuth
                    if (window.history && window.history.replaceState) {
                      const cleanUrl = window.location.pathname;
                      window.history.replaceState({}, document.title, cleanUrl);
                    }
                    
                    // Vérifier si c'est un nouvel utilisateur pour une redirection immédiate
                    const isNewUser = localStorage.getItem('isNewUser') === 'true' || 
                                      sessionStorage.getItem('isNewUser_persisted') === 'true';
                    
                    if (isNewUser) {
                      console.log("New user detected from OAuth callback, redirecting immediately");
                      localStorage.setItem('isNewUser', 'true'); // S'assurer que le flag est bien défini
                      handleRedirectAfterAuth(); // Redirection immédiate
                      return;
                    }
                    
                    // Pour les autres cas, attendre un court délai (réduit de 2000ms à 500ms)
                    setTimeout(() => {
                      // Incrémenter le compteur de redirection
                      redirectionCount++;
                      sessionStorage.setItem('redirection_attempt_count', redirectionCount.toString());
                      handleRedirectAfterAuth();
                    }, 500); // Délai réduit pour une meilleure expérience
                  } else {
                    const pendingRedirect = localStorage.getItem('redirectAfterAuth');
                    if (pendingRedirect) {
                      // Vérifier si nous sommes déjà sur la bonne page pour éviter les redirections inutiles
                      const currentPath = window.location.hash.substring(1);
                      if (currentPath === pendingRedirect) {
                        console.log(`Déjà sur la page de destination (${currentPath}), nettoyage des drapeaux de redirection`);
                        localStorage.removeItem('redirectAfterAuth');
                        localStorage.removeItem('redirectionInProgress');
                      } else {
                        console.log("Pending redirect found:", pendingRedirect);
                        // Incrémenter le compteur de redirection
                        redirectionCount++;
                        sessionStorage.setItem('redirection_attempt_count', redirectionCount.toString());
                        
                        // Ajouter un court délai avant la redirection
                        setTimeout(() => {
                          handleRedirectAfterAuth();
                        }, 500);
                      }
                    } else {
                      // Pas de redirection en attente, s'assurer que les drapeaux sont propres
                      localStorage.removeItem('redirectionInProgress');
                    }
                  }
                }
              })
              .catch(err => {
                console.error("Error in profile creation:", err);
              })
              .finally(() => {
                if (isMounted) {
                  setLoading(false);
                }
              });
          }
        } else {
          setLoading(false);
        }
      }).catch(err => {
        console.error("Error getting session:", err);
        if (isMounted) {
          setLoading(false);
        }
      });
      sessionChecked = true;
    }

    // Listen for auth changes
    const handleAuthStateChange = async (event: AuthChangeEvent, session: Session | null) => {
      if (!isMounted) return;
      
      console.log("[useAuth] Auth state changed:", event, "Session:", session?.user?.id);
      
      // Vérifier si nous sommes en cours de redirection liée à une relation
      const pendingRelationId = localStorage.getItem('pendingRelationId');
      const redirectionInProgress = localStorage.getItem('redirectionInProgress');
      
      // Si nous avons déjà une redirection en cours vers une page de partage,
      // ne pas interférer avec de nouvelles redirections
      if (pendingRelationId && redirectionInProgress === 'true') {
        console.log("[Auth] Skipping additional redirects during share page redirection");
        setUser(session?.user ?? null);
        setLoading(false);
        return;
      }
      
      const previousUser = user;
      setUser(session?.user ?? null);
      
      // Ensure profile exists when auth state changes
      if (session?.user && !previousUser) {
        ensureProfileExists(session.user)
          .then(() => {
            if (isMounted) handleRedirectAfterAuth();
          })
          .catch(err => {
            console.error("Error in profile creation:", err);
          })
          .finally(() => {
            if (isMounted) {
              setLoading(false);
            }
          });
      } else {
        setLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Helper function to ensure a profile exists for the user
  const ensureProfileExists = async (user: User) => {
    try {
      console.log("Ensuring profile exists for user:", user.id);
      
      // Vérifier si nous avons déjà essayé de créer un profil pour cet utilisateur
      const profileAttemptKey = `profile_creation_attempt_${user.id}`;
      const hasAttempted = sessionStorage.getItem(profileAttemptKey);
      
      // Vérifier si nous sommes sur la page de profil
      const isProfilePage = window.location.hash === '#/profile';
      
      // Si nous sommes sur la page de profil, laisser useProfile gérer la création du profil
      if (isProfilePage) {
        console.log("On profile page, letting useProfile handle profile creation");
        return;
      }
      
      if (hasAttempted) {
        console.log("Profile creation already attempted for this session, skipping");
        return;
      }
      
      // Marquer que nous avons tenté de créer un profil pour cet utilisateur
      sessionStorage.setItem(profileAttemptKey, 'true');
      
      // Check if profile exists
      const { data, error } = await supabase
        .from('profiles')
        .select('id, avatar_url, full_name, is_new_user')
        .eq('id', user.id)
        .single();
      
      // If no profile or error (no rows returned), create one
      if (error || !data) {
        console.log("No profile found, creating one");
        
        // Récupérer l'avatar et le nom depuis les métadonnées de l'utilisateur
        const avatarUrl = null; // Ne pas utiliser user.user_metadata?.avatar_url pour éviter les problèmes
        const fullName = user.user_metadata?.full_name || user.user_metadata?.name || null;
        
        console.log("Creating profile with name:", fullName);
        
        // Vérifier s'il y a un pendingRelationId pour ne pas marquer comme nouveau utilisateur
        const pendingRelationId = localStorage.getItem('pendingRelationId');
        const isNewUser = localStorage.getItem('isNewUser') === 'true';
        
        try {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert([{ 
              id: user.id, 
              email: user.email,
              full_name: fullName,
              avatar_url: avatarUrl,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              is_new_user: !pendingRelationId && isNewUser // Ne pas marquer comme nouveau si c'est un partage
            }]);
          
          if (insertError) {
            console.error('Error creating profile:', insertError);
          } else {
            // MODIFIÉ: Ne pas supprimer le flag isNewUser du localStorage pour permettre la redirection
            console.log('Profile created with is_new_user flag in database, preserving localStorage flag for redirection');
          }
        } catch (insertErr) {
          console.error('Exception during profile creation:', insertErr);
        }
      } else {
        console.log("Profile exists:", data);
        
        // Vérifier si le profil a besoin d'être mis à jour avec les nouvelles métadonnées
        const fullName = user.user_metadata?.full_name || user.user_metadata?.name;
        
        let needsUpdate = false;
        const updates: any = {
          updated_at: new Date().toISOString()
        };
        
        // Mettre à jour le nom seulement s'il n'existe pas déjà dans le profil
        if (fullName && !data.full_name) {
          console.log("Setting initial name from metadata:", fullName);
          updates.full_name = fullName;
          needsUpdate = true;
        }
        
        if (needsUpdate) {
          try {
            console.log("Updating profile with:", updates);
            const { error: updateError } = await supabase
              .from('profiles')
              .update(updates)
              .eq('id', user.id);
              
            if (updateError) {
              console.error('Error updating profile:', updateError);
            }
          } catch (updateErr) {
            console.error('Exception during profile update:', updateErr);
          }
        }
      }
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      console.log("[Auth] Starting signup process for:", email);

      // Vérifier s'il y a un code de parrainage dans l'URL ou dans localStorage
      const params = new URLSearchParams(window.location.search);
      let referralCode = params.get('ref');

      // Si le code n'est pas dans l'URL, vérifier dans localStorage (pour persister après redirection)
      if (!referralCode) {
        referralCode = localStorage.getItem('referral_code');
      } 

      // Si on a trouvé un code, le sauvegarder dans localStorage pour la persistance
      if (referralCode) {
        localStorage.setItem('referral_code', referralCode);
        console.log("[Auth] Code de parrainage trouvé et sauvegardé:", referralCode);
      }
      
      // Tenter directement l'inscription avec le code dans les métadonnées
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/#/login`,
          data: {
            email_confirmed: false
          }
        }
      });

      if (signUpError) {
        console.error("[Auth] Signup error:", signUpError);
        
        // Si l'erreur indique que l'utilisateur existe déjà avec Google
        if (signUpError.message?.includes('User already registered')) {
          // Vérifier si c'est un compte Google
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password: 'dummy-password-for-check'
          });

          if (signInError && signInError.message?.includes('Invalid login credentials')) {
            // L'utilisateur existe probablement avec Google
            return { error: new Error('auth.signup.existingGoogleAccount') };
          }
        }
        
        throw signUpError;
      }

      console.log("[Auth] Signup successful:", signUpData);
      
      // Marquer l'utilisateur comme nouveau
      localStorage.setItem('isNewUser', 'true');
      
      return { 
        data: signUpData,
        error: null,
        emailVerificationSent: true 
      };
    } catch (error) {
      console.error("[Auth] Signup process failed:", error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log("[Auth] Starting login with:", { email });
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;

      // Stocker un indicateur de session persistante
      localStorage.setItem('session_persistence', 'true');
      
      console.log("[Auth] Login successful, authentication completed");
      return { error: null };
    } catch (error) {
      console.error("[Auth] Login error:", error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log("[Auth] Starting Google login");
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/#/login`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) throw error;
      
      // Stocker un indicateur de session persistante
      localStorage.setItem('session_persistence', 'true');
      
      console.log("[Auth] Google auth initiated successfully");
      return { error: null };
    } catch (error) {
      console.error('[Auth] Google login error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Nettoyer les données de persistance de session
      localStorage.removeItem('session_persistence');
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      // Vérifier d'abord que le mot de passe actuel est correct
      // en essayant de se connecter avec l'email de l'utilisateur et le mot de passe actuel
      if (user && user.email) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword
        });
        
        if (signInError) {
          console.error("[Auth] Vérification du mot de passe actuel échouée:", signInError);
          return { error: new Error('settings.password.error.currentPasswordIncorrect') };
        }
        
        // Si la connexion réussit, c'est que le mot de passe actuel est correct
        // Maintenant on peut mettre à jour le mot de passe
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (error) throw error;
        return { error: null };
      } else {
        return { error: new Error('settings.password.error.userNotFound') };
      }
    } catch (error) {
      return { error };
    }
  };

  const deleteUser = async () => {
    try {
      if (!user) {
        return { error: new Error('Aucun utilisateur connecté') };
      }
      
      // 1. Appeler la fonction RPC pour nettoyer les données
      const { error: rpcError } = await supabase.rpc('delete_user', {
        user_id: user.id
      });
      
      if (rpcError) {
        console.error('[Auth] Erreur lors de la suppression des données:', rpcError);
        return { error: rpcError };
      }
      
      // 2. Déconnecter l'utilisateur
      await signOut();
      
      return { error: null };
    } catch (error) {
      console.error('[Auth] Erreur lors de la suppression du compte:', error);
      return { error };
    }
  };

  return {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    deleteUser,
  };
}